import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { 
  Mail, 
  Edit, 
  Save,
  Loader2,
  Eye,
  Code,
  FileText,
  AlertCircle,
  CheckCircle,
  Clock,
  UserPlus,
  Key,
  Shield,
  Bell
} from 'lucide-react';
import { toast } from 'sonner';

const defaultTemplates = [
  {
    id: 'password_reset',
    name: 'Wachtwoord Reset',
    subject: 'Wachtwoord herstellen - Spooker',
    description: 'Email verzonden wanneer een gebruiker een wachtwoord reset aanvraagt',
    icon: Key,
    variables: ['{{user_name}}', '{{reset_link}}', '{{expire_time}}']
  },
  {
    id: 'password_change',
    name: 'Wachtwoord Gewijzigd',
    subject: 'Wachtwoord gewijzigd - Spooker',
    description: 'Bevestiging email na succesvolle wachtwoord wijziging',
    icon: Shield,
    variables: ['{{user_name}}', '{{change_time}}']
  },
  {
    id: 'invite',
    name: 'Uitnodiging',
    subject: 'Uitnodiging voor Spooker',
    description: 'Email verzonden wanneer een nieuwe gebruiker wordt uitgenodigd',
    icon: UserPlus,
    variables: ['{{inviter_name}}', '{{organization_name}}', '{{invite_link}}']
  },
  {
    id: 'reminder',
    name: 'Re-Assessment Herinnering',
    subject: 'Tijd voor een nieuwe Re-Assessment',
    description: 'Periodieke herinnering voor security assessment',
    icon: Bell,
    variables: ['{{user_name}}', '{{last_assessment_date}}']
  },
  {
    id: 'quote_status',
    name: 'Offerte Status Update',
    subject: 'Status update van uw offerte',
    description: 'Email bij wijziging van offerte status',
    icon: FileText,
    variables: ['{{partner_name}}', '{{quote_status}}', '{{client_name}}']
  },
  {
    id: 'welcome',
    name: 'Welkom',
    subject: 'Welkom bij Spooker',
    description: 'Welkom email voor nieuwe gebruikers',
    icon: CheckCircle,
    variables: ['{{user_name}}', '{{organization_name}}', '{{login_link}}']
  }
];

const AdminEmailTemplates = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    subject: '',
    body_html: '',
    body_text: ''
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/admin/email-templates');
      // Merge with defaults
      const savedTemplates = response.data.templates || [];
      const merged = defaultTemplates.map(dt => {
        const saved = savedTemplates.find(t => t.id === dt.id);
        return saved ? { ...dt, ...saved, customized: true } : { ...dt, customized: false };
      });
      setTemplates(merged);
    } catch (error) {
      // If endpoint doesn't exist yet, use defaults
      setTemplates(defaultTemplates.map(t => ({ ...t, customized: false })));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setEditForm({
      subject: template.subject || '',
      body_html: template.body_html || getDefaultHtmlTemplate(template.id),
      body_text: template.body_text || ''
    });
  };

  const getDefaultHtmlTemplate = (templateId) => {
    const templates = {
      password_reset: `<h2>Wachtwoord herstellen</h2>
<p>Beste {{user_name}},</p>
<p>U heeft een verzoek ingediend om uw wachtwoord te herstellen.</p>
<p>Klik op de onderstaande link om uw wachtwoord te resetten:</p>
<p><a href="{{reset_link}}">Wachtwoord herstellen</a></p>
<p>Deze link is geldig tot {{expire_time}}.</p>
<br>
<p>Met vriendelijke groet,<br>Het Spooker Team</p>`,
      password_change: `<h2>Wachtwoord gewijzigd</h2>
<p>Beste {{user_name}},</p>
<p>Uw wachtwoord voor Spooker is zojuist gewijzigd.</p>
<p><strong>Tijdstip:</strong> {{change_time}}</p>
<p>Als u deze wijziging niet heeft gedaan, neem dan direct contact op met support.</p>
<br>
<p>Met vriendelijke groet,<br>Het Spooker Team</p>`,
      invite: `<h2>Uitnodiging voor Spooker</h2>
<p>Beste,</p>
<p>{{inviter_name}} heeft u uitgenodigd om deel te nemen aan {{organization_name}} op Spooker.</p>
<p>Klik op de onderstaande link om uw account te activeren:</p>
<p><a href="{{invite_link}}">Account activeren</a></p>
<br>
<p>Met vriendelijke groet,<br>Het Spooker Team</p>`,
      reminder: `<h2>Tijd voor Re-Assessment</h2>
<p>Beste {{user_name}},</p>
<p>Het is tijd om uw security status opnieuw te beoordelen.</p>
<p>Een regelmatige re-assessment helpt u om uw vooruitgang bij te houden en nieuwe risico's te identificeren.</p>
<br>
<p>Met vriendelijke groet,<br>Het Spooker Team</p>`,
      welcome: `<h2>Welkom bij Spooker</h2>
<p>Beste {{user_name}},</p>
<p>Welkom bij Spooker! Uw account is succesvol aangemaakt.</p>
<p>U bent nu onderdeel van {{organization_name}}.</p>
<p><a href="{{login_link}}">Klik hier om in te loggen</a></p>
<br>
<p>Met vriendelijke groet,<br>Het Spooker Team</p>`
    };
    return templates[templateId] || '<p>Template content hier...</p>';
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/email-templates/${editingTemplate.id}`, {
        subject: editForm.subject,
        body_html: editForm.body_html,
        body_text: editForm.body_text
      });
      toast.success('Template opgeslagen');
      setTemplates(prev => prev.map(t => 
        t.id === editingTemplate.id 
          ? { ...t, ...editForm, customized: true }
          : t
      ));
      setEditingTemplate(null);
    } catch (error) {
      toast.error('Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = (template) => {
    setPreviewTemplate(template);
  };

  const resetToDefault = async (templateId) => {
    try {
      await api.delete(`/admin/email-templates/${templateId}`);
      toast.success('Template gereset naar standaard');
      setTemplates(prev => prev.map(t => 
        t.id === templateId 
          ? { ...defaultTemplates.find(dt => dt.id === templateId), customized: false }
          : t
      ));
    } catch (error) {
      toast.error('Reset mislukt');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-neutral-400" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">
            Email Templates
          </h1>
          <p className="text-neutral-600 dark:text-neutral-400">
            Beheer en pas email templates aan die naar gebruikers worden verzonden
          </p>
        </div>

        {/* Info Card */}
        <Card className="border-blue-200 bg-blue-50 dark:bg-blue-950 dark:border-blue-800">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-900 dark:text-blue-100">Template Variabelen</p>
                <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                  Gebruik variabelen zoals {"{{user_name}}"} om dynamische content toe te voegen. 
                  Beschikbare variabelen worden per template getoond.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Templates Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {templates.map((template) => {
            const IconComponent = template.icon || Mail;
            return (
              <Card key={template.id} className="border-neutral-200 dark:border-neutral-800">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center">
                        <IconComponent className="h-5 w-5 text-neutral-600 dark:text-neutral-400" />
                      </div>
                      <div>
                        <CardTitle className="text-base">{template.name}</CardTitle>
                        <CardDescription className="text-xs mt-0.5">
                          {template.description}
                        </CardDescription>
                      </div>
                    </div>
                    {template.customized && (
                      <Badge className="bg-green-100 text-green-700 border-green-200">
                        Aangepast
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="text-sm">
                      <span className="text-neutral-500">Onderwerp:</span>
                      <span className="ml-2 text-neutral-900 dark:text-white">{template.subject}</span>
                    </div>
                    
                    <div className="flex flex-wrap gap-1">
                      {template.variables?.map((v, i) => (
                        <Badge key={i} variant="outline" className="text-xs font-mono">
                          {v}
                        </Badge>
                      ))}
                    </div>
                    
                    <Separator />
                    
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => handlePreview(template)}
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        Voorbeeld
                      </Button>
                      <Button 
                        size="sm" 
                        className="flex-1"
                        onClick={() => handleEdit(template)}
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Bewerken
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editingTemplate} onOpenChange={() => setEditingTemplate(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Template Bewerken: {editingTemplate?.name}</DialogTitle>
            <DialogDescription>
              Pas de email template aan. Gebruik variabelen voor dynamische content.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Beschikbare variabelen</Label>
              <div className="flex flex-wrap gap-2">
                {editingTemplate?.variables?.map((v, i) => (
                  <Badge 
                    key={i} 
                    variant="outline" 
                    className="font-mono cursor-pointer hover:bg-neutral-100"
                    onClick={() => {
                      navigator.clipboard.writeText(v);
                      toast.success('Gekopieerd naar klembord');
                    }}
                  >
                    {v}
                  </Badge>
                ))}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="subject">Onderwerp</Label>
              <Input
                id="subject"
                value={editForm.subject}
                onChange={(e) => setEditForm(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="Email onderwerp..."
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="body_html">
                <div className="flex items-center gap-2">
                  <Code className="h-4 w-4" />
                  HTML Content
                </div>
              </Label>
              <Textarea
                id="body_html"
                value={editForm.body_html}
                onChange={(e) => setEditForm(prev => ({ ...prev, body_html: e.target.value }))}
                placeholder="<h2>Titel</h2><p>Content...</p>"
                className="min-h-[300px] font-mono text-sm"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="body_text">Platte tekst (fallback)</Label>
              <Textarea
                id="body_text"
                value={editForm.body_text}
                onChange={(e) => setEditForm(prev => ({ ...prev, body_text: e.target.value }))}
                placeholder="Platte tekst versie voor email clients die geen HTML ondersteunen..."
                className="min-h-[100px]"
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            {editingTemplate?.customized && (
              <Button 
                variant="outline" 
                onClick={() => {
                  resetToDefault(editingTemplate.id);
                  setEditingTemplate(null);
                }}
                className="mr-auto"
              >
                Reset naar standaard
              </Button>
            )}
            <Button variant="outline" onClick={() => setEditingTemplate(null)}>
              Annuleren
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Opslaan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewTemplate} onOpenChange={() => setPreviewTemplate(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Voorbeeld: {previewTemplate?.name}</DialogTitle>
            <DialogDescription>
              Onderwerp: {previewTemplate?.subject}
            </DialogDescription>
          </DialogHeader>
          
          <div className="border rounded-lg p-4 bg-white min-h-[300px]">
            <div 
              className="prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ 
                __html: previewTemplate?.body_html || getDefaultHtmlTemplate(previewTemplate?.id) 
              }}
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewTemplate(null)}>
              Sluiten
            </Button>
            <Button onClick={() => {
              setPreviewTemplate(null);
              handleEdit(previewTemplate);
            }}>
              <Edit className="h-4 w-4 mr-2" />
              Bewerken
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminEmailTemplates;
